<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Pedidos</p>
        </div>

        <div class="muven-content">
            <Loading page v-if="loading" />

            <ConfirmationExclusionModalCmp 
                ref="confirmationExclusionModal"
                @confirm="confirmExclusionHandler" />

            <AuditListQuickVisualizationCmp 
                ref="auditListQuickVisualizationModal" />

            <div class="mb-3 order-toolbar" v-if="!loading">
                <div class="order-toolbar-actions">
                    <button type="button"
                        class="btn btn-primary"
                        @click="showFilterModal()">
                        Filtro
                    </button>

                    <button type="button"
                        class="btn btn-outline-secondary ml-3"
                        @click="openOrderImportSpecificModal()">
                        Importar pedido
                    </button>

                    <button type="button"
                        class="btn btn-outline-secondary ml-3"
                        v-if="subscriber.isEnviromentHML()"
                        :disabled="loadingFakeOrderModalInfo"
                        @click="showFakeOrderModal()">
                        Criar pedido de teste

                        <Loading v-show="loadingFakeOrderModalInfo" />
                    </button>
                </div>

                <div class="order-view-mode" role="group" aria-label="Formato de exibição da lista">
                    <span class="order-view-mode-caption">Exibir como</span>

                    <button type="button"
                        class="order-view-mode-option"
                        :class="{'is-active': isTimeline}"
                        :aria-pressed="isTimeline ? 'true' : 'false'"
                        @click="changeViewMode(VIEW_MODE_TIMELINE)">Linha do tempo</button>

                    <button type="button"
                        class="order-view-mode-option"
                        :class="{'is-active': !isTimeline}"
                        :aria-pressed="isTimeline ? 'false' : 'true'"
                        @click="changeViewMode(VIEW_MODE_FLAT)">Lista</button>
                </div>
            </div>

            <FilterInformationCmp 
                v-if="!loading"
                :filter-submitted="orderFilterSubmitted"
                :filter-properties="orderFilterProperties" />

            <OrderFlowCmp 
                ref="orderFlow" 
                @status-changed="statusChangedHandler" />

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-import-specific-modal">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                <span>Importação de pedido</span>
                            </h5>
                        </div>
                        <div class="modal-body">
                            <div>
                                <div class="row">
                                    <div class="col-12">
                                        <div class="muven-tip tip-info mb-3">
                                            <fa icon="info" class="tip-icon" />
                                            <p><b>ATENÇÃO!</b></p>
                                            <p>Esta funcionalidade apenas deve ser utilizada nos casos em que o Muven não recebeu o pedido do canal.</p>
                                            <p>Não utilize este formulário para atualizar as informações de um pedido já existente.</p>
                                        </div>
                                    </div>

                                    <div class="col-12 form-group">
                                        <label>Canal</label>
                                        <multiselect
                                            v-model="orderImportSpecific.channel"
                                            :options="channelListOptions"
                                            :multiple="false"
                                            :show-labels="false"
                                            label="name"
                                            track-by="enumChannel"
                                            placeholder="" />
                                    </div>

                                    <div class="col-12 form-group">
                                        <label>Código do pedido</label>
                                        <input type="text" 
                                            class="form-control"
                                            v-model="orderImportSpecific.idExternalChannel">
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="importSpecificOrder(orderImportSpecific)"
                                :disabled="importingOrderSpecific || !orderImportSpecific.isValid()">
                                <fa icon="sync-alt" class="mr-1" spin v-show="importingOrderSpecific"/>

                                Importar
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeOrderImportSpecificModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-reimport-confirmation-modal">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                <span>Reimportação de pedido</span>
                            </h5>
                        </div>
                        <div class="modal-body">
                            <div>
                                <p>Ao continuar, o pedido <b>{{chosenOrderReimport.idExternalChannel}}</b> do canal <b>{{chosenOrderReimport.channel.name}}</b> será reimportado.</p>
                                <p>Porém, para que não haja duplicação, o pedido anterior será cancelado tanto no Muven quanto na retaguarda. Mas não se preocupe, pois o status do canal não será alterado.</p>
                                <p class="mt-3">Tem certeza que deseja reimportar o pedido <b>{{chosenOrderReimport.idExternalChannel}}</b>?</p>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-outline-danger" 
                                @click="reimportOrder(chosenOrderReimport)"
                                :disabled="reimportingOrder">
                                <fa icon="exclamation-triangle" class="mr-1" v-show="!reimportingOrder"/>
                                <fa icon="sync-alt" class="mr-1" spin v-show="reimportingOrder"/>

                                Sim, reimportar
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeOrderReimportConfirmationModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-list-filter-modal">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Filtro de pedidos</h5>
                        </div>
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-12 form-group">
                                    <label>Tipo de filtro</label>
                                    <div class="btn-group">
                                       <button v-for="(filterOption, indexFilterOption) in filterOptions" :key="indexFilterOption"
                                        type="button" 
                                        class="btn btn-outline-secondary"
                                        :class="{active: filterOption.active}"
                                        @click="updateFilterOption(filterOption)">{{filterOption.description}}</button>
                                    </div>
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByTrackingCodeInfo">
                                <div class="col-12 form-group">
                                    <label>Código de rastreio</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.trackingCode" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByChannelInfo">
                                <div class="col-12 form-group">
                                    <label>Id no canal</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.idExternalChannel" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByBackendInfo">
                                <div class="col-12 form-group">
                                    <label>Código na retaguarda</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.idSource" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionAdvanced">
                                <div class="col-12 col-md-6 form-group">
                                    <label>Data inicial de criação no Muven</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.dateCreatedAtMuven.start" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Data final de criação no Muven</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.dateCreatedAtMuven.end" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Data inicial de venda no canal</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.date.start" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Data final de venda no canal</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.date.end" />
                                </div>

                                <div class="col-12 form-group" v-if="hasAccessToMoreThanOneChannel">
                                    <label>Canal</label>
                                    <multiselect
                                        v-model="orderFilter.channelList"
                                        :options="channelListOptions"
                                        :multiple="true"
                                        :show-labels="false"
                                        label="name"
                                        track-by="enumChannel"
                                        placeholder="" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Nome do cliente</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.customer" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Chave de acesso NFe</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.accessKey" />
                                </div>

                                <div class="col-12 form-group">
                                    <label>Status do pedido</label>
                                    <SelectWrapperCmp 
                                        :show-remove-button="!!orderFilter.statusList.length"
                                        @removed="handleOrderStatusListRemoval">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="orderFilter.statusList"
                                                :options="statusListOptions"
                                                :multiple="true"
                                                :show-labels="false"
                                                label="description"
                                                track-by="enum"
                                                placeholder="" />
                                        </template>
                                    </SelectWrapperCmp>
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Status da importação na retaguarda</label>
                                    <multiselect
                                        v-model="orderFilter.statusIdSource"
                                        :options="statusIdSourceListOptions"
                                        :multiple="false"
                                        :show-labels="false"
                                        label="description"
                                        track-by="enum"
                                        placeholder="" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Status do código de rastreio</label>
                                    <multiselect
                                        v-model="orderFilter.statusTrackingCode"
                                        :options="statusTrackingCodeListOptions"
                                        :multiple="false"
                                        :show-labels="false"
                                        label="description"
                                        track-by="enum"
                                        placeholder="" />
                                </div>

                                <div class="form-group col-12" v-show="hasMoreThanOneStore">
                                    <label>Loja</label>
                                    <SelectWrapperCmp 
                                        :show-remove-button="!!orderFilter.storeList.length"
                                        @removed="handleOrderStoreListRemoval">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="orderFilter.storeList"
                                                :options="storeList"
                                                :multiple="true"
                                                :show-labels="false"
                                                placeholder=""
                                                label="name"
                                                track-by="id" />
                                        </template>
                                    </SelectWrapperCmp>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="getOrdersWithFilter(orderFilter)"
                                :disabled="loadingFilter">
                                    Filtrar
                                    <Loading v-show="loadingFilter" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="clearFilter()">Restaurar</button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeFilterModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <OrderFakeCmp 
                ref="orderFakeModal" 
                :store-list="storeList"
                :channel-list-options="channelListOptions"
                @submitted="handleOrderFakeSubmission" />

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-tracking-code-modal">
                <div class="modal-dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Atualizar código de rastreio</h5>
                            <p class="modal-subtitle">Código: {{orderSelectedTrackingCode.idExternalChannel}}</p>
                        </div>
                        <div class="modal-body">
                            <div>
                                <div class="form-group">
                                    <label>Código de rastreio</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderTrackingCode.trackingCode" />
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="updateOrderTrackingCode(orderSelectedTrackingCode, orderTrackingCode)"
                                :disabled="!orderTrackingCode.isValid() || loadingUpdatingOrderTrackingCode">
                                    Gravar
                                    <Loading v-show="loadingUpdatingOrderTrackingCode" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeTrackingCodeModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <NothingFound v-if="nothingToShow" />

            <div v-if="!loading && !nothingToShow">
                <div :class="{'order-timeline': isTimeline}">
                    <div class="table-responsive">
                        <table class="table table-sm table-hover">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Id canal</th>
                                    <th>Canal</th>
                                    <th v-show="hasMoreThanOneStore">Loja</th>
                                    <th :class="isTimeline ? 'time-col' : 'datetime-col'">{{isTimeline ? 'Hora' : 'Momento venda'}}</th>
                                    <th class="status-col">Importação ret.</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>

                            <tbody v-for="group in displayGroups" :key="group.key">
                                <tr v-if="!group.isHeaderless"
                                    class="timeline-day"
                                    :class="{'is-today': group.isToday, 'is-open-day': group.isOpen}">
                                    <td :colspan="columnCount">
                                        <span class="timeline-day-label">{{group.label}}</span>
                                        <span class="timeline-day-count"
                                            :class="{'is-open': group.isOpen}"
                                            :title="group.isOpen ? 'Podem existir mais pedidos deste dia nas próximas páginas' : null">
                                            {{groupCountLabel(group)}}
                                        </span>
                                    </td>
                                </tr>

                                <tr v-for="order in group.orders"
                                    :key="order.id"
                                    class="order-row"
                                    :class="{'is-open-day': group.isOpen}">
                                    <td>{{customerName(order) | infoOrPlaceholder}}</td>
                                    <td>{{order.idExternalChannel | infoOrPlaceholder}}</td>
                                    <td><ChannelLogoCmp :channel="order.channel" /></td>
                                    <td v-show="hasMoreThanOneStore">{{order.store.idSource | infoOrPlaceholder}}</td>
                                    <td :title="orderDateTitle(order)">{{orderDateLabel(order) | infoOrPlaceholder}}</td>
                                    <td>
                                        <span class="muven-td-icon">
                                            <fa icon="hourglass-start" 
                                                v-if="!order.hasIdSource()"
                                                class="text-muted" />

                                            <fa icon="check-circle" 
                                                v-if="order.hasIdSource()"
                                                class="text-success" />   
                                        </span>
                                        <span v-if="!order.hasIdSource()">Aguardando</span>
                                        <span v-if="order.hasIdSource()">Importado</span>
                                    </td>
                                    <td>
                                        <span class="muven-td-icon">
                                            <fa icon="hourglass-start" 
                                                v-if="order.isPending()"
                                                class="text-muted" />

                                            <fa icon="cloud-download-alt" 
                                                v-if="order.isIntegrating()"
                                                class="text-muted" />

                                            <fa icon="check-circle" 
                                                v-if="order.isApproved()"
                                                class="text-success" />

                                            <fa icon="money-bill-wave" 
                                                v-if="order.isBilled()"
                                                class="text-success" />

                                            <fa icon="truck-loading" 
                                                v-if="order.isPosted()"
                                                class="text-success" />

                                            <fa icon="handshake" 
                                                v-if="order.isDelivered()"
                                                class="text-success" />

                                            <fa icon="window-close" 
                                                v-if="order.isCanceled()"
                                                class="text-danger" />

                                            <fa icon="flag-checkered" 
                                                v-if="order.isFinalized()"
                                                class="text-success" />

                                            <fa icon="exclamation-triangle" 
                                                v-if="order.isError()"
                                                class="text-danger" />

                                            <fa icon="link" 
                                                v-if="order.isPendingAssignment()"
                                                class="text-warning" />
                                        </span>
                                        <span>{{order.getOrderStatusEnumDescription()}}</span>
                                    </td>
                                    <td class="text-center muven-td-with-buttons">
                                        <button type="button" 
                                            class="btn btn-default text-muted" 
                                            data-toggle="dropdown" >
                                            <fa icon="ellipsis-v" v-show="!order.loadingListAction" />
                                            <fa icon="sync-alt" spin v-show="order.loadingListAction" />
                                        </button>

                                        <div class="dropdown-menu dropdown-menu-right muven-action-menu">
                                            <router-link class="dropdown-item"
                                                :to="detailUrl(order)">
                                                <fa icon="file-alt" fixed-width class="muven-action-icon" />
                                                <span class="muven-action-label">Detalhes</span>
                                            </router-link>

                                            <div>
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item"
                                                    v-if="order.channel.isMuvenCart()"
                                                    @click="openFlowModal(order)">
                                                    <fa icon="hourglass-half" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Andamento do pedido</span>
                                                </span>

                                                <span class="dropdown-item"
                                                    v-if="order.canObtainInfoFromChannel()"
                                                    @click="forceRetrySync(order)">
                                                    <fa icon="cloud-download-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Obter atualização do canal</span>
                                                </span>

                                                <span class="dropdown-item"
                                                    v-if="order.canForceSync()"
                                                    @click="forceChannelSync(order)">
                                                    <fa icon="cloud-upload-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Forçar atualização no canal</span>
                                                </span>

                                                <span class="dropdown-item"
                                                    @click="showTrackingCodeModal(order)">
                                                    <fa icon="barcode" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Atualizar código de rastreio</span>
                                                </span>
                                            </div>

                                            <div v-if="order.canBeReimported()">
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item"
                                                    @click="openOrderReimportConfirmationModal(order)">
                                                    <fa icon="redo-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Reimportar pedido</span>
                                                </span>
                                            </div>

                                            <div v-if="order.canBeDeleted()">
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item muven-action-danger"
                                                    @click="openConfirmationExclusionModal(order)">
                                                    <fa icon="trash-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Excluir pedido</span>
                                                </span>
                                            </div>

                                            <div>
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item"
                                                    @click="showAuditQuickVisualization(order)">
                                                    <fa icon="search" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Auditoria rápida</span>
                                                </span>

                                                <a class="dropdown-item"
                                                    :href="getAuditUrl(order.id, EntityEnum.Order)"
                                                    target="_blank"
                                                    rel="noopener"
                                                    title="Abre em uma nova aba">
                                                    <fa icon="history" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Ir para auditoria</span>
                                                    <fa icon="external-link-alt" class="muven-action-external" />
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="openDayGroup" class="timeline-continues"></div>

                    <ListCount :amount="orders.length" />

                    <p v-if="openDayGroup" class="timeline-open-day-note">
                        <span v-if="openDayLabel">O dia {{openDayLabel}} pode continuar nas próximas páginas.</span>
                        <span v-else>A lista pode continuar nas próximas páginas.</span>
                    </p>
                </div>

                <div v-if="showPaginationButton" class="text-center">
                    <button class="btn btn-outline-secondary"
                        @click="getMoreOrders()"
                        :disabled="loadingNextPage">
                        <span v-show="!loadingNextPage">
                            Mais resultados<span v-if="openDayLabel" class="pagination-day-hint"> ({{openDayLabel}} em diante)</span>
                        </span>
                        <fa v-show="loadingNextPage" icon="sync-alt" spin/>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {cloneDeep} from 'lodash'
import orderService from '@/order/services/OrderService'
import {PaginationModel} from '@/utils/models/PaginationModel'
import {dateTimeFilter, timeFilter, dayMonthFilter} from '@/utils/filters/DateTimeFilter'
import {groupOrdersByDay} from '@/order/normalizers/OrderTimelineNormalizer'
import orderListPreferencesManager, {VIEW_MODE_TIMELINE, VIEW_MODE_FLAT} from '@/order/managers/OrderListPreferencesManager'
import {OrderListFilterModel} from '@/order/models/OrderListFilterModel'
import modalService from '@/utils/services/ModalService'
import { OrderModel } from '@/order/models/OrderModel'
import { OrderTrackingCodeModel } from '@/order/models/OrderTrackingCodeModel'
import channelService from '@/channel/services/ChannelService'
import FilterInformationCmp from '@/utils/components/FilterInformationCmp'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '@/utils/mixins/GetAuditUrl'
import {infoOrPlaceholder} from '@/utils/filters/InformationOrPlaceholderFilter'
import SelectWrapperCmp from '@/utils/components/SelectWrapperCmp'
import OrderFlowCmp from "@/order/components/OrderFlowCmp"
import { StoreModel } from '@/store/models/StoreModel'
import productService from '@/product/services/ProductService'
import { ProductModel } from '../../product/models/ProductModel'
import ConfirmationExclusionModalCmp from '@/utils/components/ConfirmationExclusionModalCmp'
import OrderFakeCmp from '@/order/components/OrderFakeCmp.vue'
import AuditListQuickVisualizationCmp from '@/audit/components/AuditListQuickVisualizationCmp'
import { OrderImportSpecificModel } from '@/order/models/OrderImportSpecificModel'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp'

const ID_TRACKING_CODE_MODAL = '#muven-order-tracking-code-modal'
const ID_ORDER_REIMPORT_MODAL = '#muven-order-reimport-confirmation-modal'    
const ID_ORDER_IMPORT_SPECIFIC_MODAL = '#muven-order-import-specific-modal'
const ID_FILTER_MODAL = '#muven-order-list-filter-modal'
const FILTER_BY_BACKEND_INFO = 'filter_by_backend_info'
const FILTER_BY_CHANNEL_INFO = 'filter_by_channel_info'
const FILTER_BY_TRACKING_CODE = 'filter_by_tracking_code'
const FILTER_ADVANCED = 'filter_advanced'

export default {
    name: 'OrderListView',
    components: {
        FilterInformationCmp,
        SelectWrapperCmp,
        OrderFlowCmp,
        ConfirmationExclusionModalCmp,
        OrderFakeCmp,
        AuditListQuickVisualizationCmp,
        ChannelLogoCmp,
    },
    mixins: [
        GetAuditUrl,
    ],
    filters: {
        infoOrPlaceholder,
    },
    data() {
        return {
            EntityEnum,
            VIEW_MODE_TIMELINE,
            VIEW_MODE_FLAT,
            viewMode: orderListPreferencesManager.getViewMode(),
            loading: true,
            loadingNextPage: false,
            loadingFilter: false,
            loadingUpdatingOrderTrackingCode: false,
            showPaginationButton: true,
            reimportingOrder: false,
            importingOrderSpecific: false,
            loadingFakeOrderModalInfo: false,
            pagination: new PaginationModel(),
            orders: [],
            orderSelectedTrackingCode: new OrderModel(),
            orderImportSpecific: new OrderImportSpecificModel(),
            orderTrackingCode: new OrderTrackingCodeModel(),
            statusListOptions: OrderListFilterModel.statusList(),
            statusTrackingCodeListOptions: OrderListFilterModel.statusTrackingCodeList(),
            statusIdSourceListOptions: OrderListFilterModel.statusIdSourceList(),
            channelListOptions: [], // get from channelService.getChannels()
            orderFilter: new OrderListFilterModel(),
            orderFilterSubmitted: new OrderListFilterModel(),
            chosenOrderReimport: new OrderModel(),
            filterOptions: [
                {
                    enum: FILTER_ADVANCED,
                    description: 'Geral',
                    active: true,
                },
                {
                    enum: FILTER_BY_BACKEND_INFO,
                    description: 'Retaguarda',
                    active: false,
                },
                {
                    enum: FILTER_BY_CHANNEL_INFO,
                    description: 'Canal',
                    active: false,
                },
                {
                    enum: FILTER_BY_TRACKING_CODE,
                    description: 'Rastreio',
                    active: false,
                },
            ],
            filterOptionSelected: {
                enum: FILTER_ADVANCED,
                description: 'Geral',
                active: true,
            },
            orderFilterProperties: [
                {
                    type: 'daterange',
                    label: 'Período de compra',
                    property: 'date',
                },
                {
                    type: 'daterange',
                    label: 'Período de criação no Muven',
                    property: 'dateCreatedAtMuven',
                },
                {
                    type: 'string',
                    label: 'Cliente',
                    property: 'customer',
                },
                {
                    type: 'string',
                    label: 'Chave de acesso',
                    property: 'accessKey',
                },
                {
                    type: 'list',
                    label: 'Canal',
                    property: 'channelList',
                    propertyInList: 'name',
                },
                {
                    type: 'list',
                    label: 'Status',
                    property: 'statusList',
                    propertyInList: 'description',
                },
                {
                    type: 'string',
                    label: 'Código de rastreio',
                    property: 'trackingCode',
                },
                {
                    type: 'enumwrapped',
                    label: 'Status do código de rastreio',
                    property: 'statusTrackingCode',
                },
                {
                    type: 'string',
                    label: 'Id na retaguarda',
                    property: 'idSource',
                },
                {
                    type: 'enumwrapped',
                    label: 'Status da importação na retaguarda',
                    property: 'statusIdSource',
                },
                {
                    type: 'string',
                    label: 'Id no canal',
                    property: 'idExternalChannel',
                },
                {
                    type: 'list',
                    label: 'Loja',
                    property: 'storeList',
                    propertyInList: 'name',
                },
                {
                    type: 'list',
                    label: 'Pedido com o produto',
                    property: 'productIdList',
                    propertyInList: 'name',
                },
            ]
        }
    },
    async mounted() {
        try {
            const querystring = this.$route.query
            this.orderFilter = new OrderListFilterModel(querystring)

            for (const prod of this.orderFilter.productIdList) {
                const p = ProductModel.normalizeV1(await productService.getProductById(prod.id), this.subscriber)

                if (p.isSku && p.hasAttributeTypeGrid()) {
                    prod.name = `${p.name} (${p.getVariationsStringified()})`
                } else {
                    prod.name = p.name
                }
            }

            this.orders = await orderService.getOrders(this.pagination.actual(), this.orderFilter)
            this.channelListOptions = await channelService.getChannels()
            this.pagination.next()

            if (querystring) {                
                this.orderFilterSubmitted = cloneDeep(this.orderFilter)
            }
        } catch(e) {
            this.$floatingAlert.error({
                message: e.message,
            })
        }

        this.loading = false
    },
    methods: {
        changeViewMode(mode) {
            this.viewMode = mode
            orderListPreferencesManager.setViewMode(mode)
        },
        // Na timeline o dia já está no cabeçalho do grupo, então a célula mostra só a
        // hora. Sem cabeçalho essa informação se perderia, e a data completa volta.
        orderDateLabel(order) {
            return this.isTimeline ? timeFilter(order.createdAt) : dateTimeFilter(order.createdAt)
        },
        orderDateTitle(order) {
            return this.isTimeline ? dateTimeFilter(order.createdAt) : null
        },
        groupCountLabel(group) {
            // Num dia ainda em aberto o plural vale sempre: existe pelo menos mais um possível.
            if (group.isOpen) {
                return `${group.orders.length}+ pedidos`
            }

            return `${group.orders.length} ${group.orders.length === 1 ? 'pedido' : 'pedidos'}`
        },
        async getMoreOrders() {
            try {
                this.loadingNextPage = true
                const newOrders = await orderService.getOrders(this.pagination.actual(), this.orderFilter)
                this.orders.push(...newOrders)

                if (!newOrders.length) {
                    this.showPaginationButton = false
                }

                this.pagination.next()
            } catch(e) {
               this.$floatingAlert.error({
                   message: e.message,
               })               
            }
            
            this.loadingNextPage = false
        },
        async getOrdersWithFilter(filterList) {
            this.showPaginationButton = true
            
            if (filterList.hasTooSpecificFilter()) {
                filterList.clearStatusList()
            }

            this.orderFilterSubmitted = cloneDeep(filterList)
            this.pagination.reset()
            this.loadingFilter = true
            try {
                this.orders = await orderService.getOrders(this.pagination.actual(), filterList)
                this.closeFilterModal()
                this.pagination.next()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingFilter = false
        },
        updateFilterOption(filterOption) {
            this.filterOptions.forEach((fo) => {
                fo.active = false
            })

            this.filterOptionSelected = Object.assign(filterOption, {active: true})            
            this.orderFilter = new OrderListFilterModel()

            if (!this.isFilterOptionAdvanced) {
                this.orderFilter.clearStatusList()
            } 
        },
        openConfirmationExclusionModal(order) {
            const info = {
                id: order.id,
                title: 'Exclusão de pedido',
                message: `Tem certeza que deseja excluir o pedido <span style="font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: 1px;">${order.idExternalChannel}</span>?`,
            }

            this.$refs.confirmationExclusionModal.open(info)
        },
        async reimportOrder(order) {
            this.reimportingOrder = true;

            try {
                await orderService.reimport(order.id)

                this.$floatingAlert.info({
                    message: `Solicitação de reimportação do pedido <b>${order.idExternalChannel}</b> no canal <b>${order.channel.name}</b> registrada com sucesso.`,
                    timeVisible: 10000,
                })

                this.closeOrderReimportConfirmationModal()
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.reimportingOrder = false;
        },
        async importSpecificOrder(specificOrder) {
            this.importingOrderSpecific = true;

            try {
                await orderService.importSpecific(specificOrder.channel.id, specificOrder.idExternalChannel)

                this.$floatingAlert.info({
                    message: `Solicitação de importação do pedido <b>${specificOrder.idExternalChannel}</b> no canal <b>${specificOrder.channel.name}</b> registrada com sucesso.`,
                    timeVisible: 10000,
                })

                this.closeOrderImportSpecificModal()
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.importingOrderSpecific = false;
        },
        openOrderReimportConfirmationModal(order) {
            this.chosenOrderReimport = cloneDeep(order);
            modalService.show(ID_ORDER_REIMPORT_MODAL);
        },
        closeOrderReimportConfirmationModal() {
            modalService.hide(ID_ORDER_REIMPORT_MODAL);
        },
        openOrderImportSpecificModal() {
            modalService.show(ID_ORDER_IMPORT_SPECIFIC_MODAL);
        },
        closeOrderImportSpecificModal() {
            modalService.hide(ID_ORDER_IMPORT_SPECIFIC_MODAL);
        },
        async confirmExclusionHandler({id}) {
            try {
                await orderService.deleteOrder(id)

                for (let i = 0; i < this.orders.length; i++) {
                    if (this.orders[i].id === id) {
                        this.$floatingAlert.success({
                            message: `Pedido <span style="font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: 1px;">${this.orders[i].idExternalChannel}</span> excluído com sucesso.`,
                        })
                        this.orders.splice(i, 1)
                        break
                    }
                }
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.$refs.confirmationExclusionModal.close()
        },
        async showFilterModal() {
            await modalService.show(ID_FILTER_MODAL)
        },
        async closeFilterModal() {
            await modalService.hide(ID_FILTER_MODAL)
        },
        async showFakeOrderModal() {
            this.loadingFakeOrderModalInfo = true;
            await this.$refs.orderFakeModal.showModal()
            this.loadingFakeOrderModalInfo = false;
        },
        async showTrackingCodeModal(order) {
            this.orderSelectedTrackingCode = order;
            this.orderTrackingCode = new OrderTrackingCodeModel()
            await modalService.show(ID_TRACKING_CODE_MODAL)
        },
        async closeTrackingCodeModal() {
            await modalService.hide(ID_TRACKING_CODE_MODAL)
        },
        async updateOrderTrackingCode(order, trackingCode) {
            this.loadingUpdatingOrderTrackingCode = true

            try {
                await orderService.updateTrackingCode(order.id, trackingCode)
                this.$floatingAlert.success({
                    message: 'Código de rastreio atualizado com sucesso!',
                })
                this.closeTrackingCodeModal()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingUpdatingOrderTrackingCode = false
        },
        async forceRetrySync(order) {
            order.loadingListAction = true

            try {
                const info = await orderService.retrySync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            order.loadingListAction = false
        },
        async forceChannelSync(order) {
            order.loadingListAction = true

            try {
                const info = await orderService.forceChannelSync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            order.loadingListAction = false
        },
        handleOrderFakeSubmission(order) {
            this.orders.unshift(order)
        },
        goto(order) {
            this.$router.push(`/pedidos/${order.id}`)
        },
        clearFilter() {
            this.orderFilter = new OrderListFilterModel()
        },
        handleOrderStatusListRemoval() {
            this.orderFilter.clearStatusList()
        },
        async openFlowModal(order) {
            await this.$refs.orderFlow.openModal(order.id);
        },
        statusChangedHandler(info) {
            for (const order of this.orders) {
                if (order.isEqual(info)) {
                    order.setStatus(info.status);
                    break;
                }
            }
        },
        handleOrderStoreListRemoval() {
            this.orderFilter.storeList = []
        },
        async showAuditQuickVisualization(order) {
            order.loadingListAction = true

            await this.$refs.auditListQuickVisualizationModal.showModal({
                idEntity: order.id,
                entityList: EntityEnum.Order,
                getDescription: () => `Pedido: ${order.idExternalChannel}`,
            });

            order.loadingListAction = false
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
            storeList: (state) => {
                return StoreModel.getStoreListWithIdAndName(state.storeList)
            },
        }),
        ...mapGetters([
            'hasMoreThanOneStore',
        ]),
        nothingToShow() {
            return !this.loading && !this.orders.length
        },
        isTimeline() {
            return this.viewMode === VIEW_MODE_TIMELINE
        },
        orderGroups() {
            return groupOrdersByDay(this.orders, this.showPaginationButton)
        },
        // Na lista clássica tudo cai num grupo único e sem cabeçalho. É o que permite
        // manter um só v-for de linhas atendendo aos dois modos.
        displayGroups() {
            if (this.isTimeline) {
                return this.orderGroups
            }

            return [{key: 'all', orders: this.orders, isHeaderless: true}]
        },
        // O último dia da lista é o único cuja contagem ainda pode crescer na paginação.
        // Sem contagens por dia na tela, a ressalva não tem o que qualificar.
        openDayGroup() {
            if (!this.isTimeline) {
                return null
            }

            const lastGroup = this.orderGroups[this.orderGroups.length - 1]

            return lastGroup && lastGroup.isOpen ? lastGroup : null
        },
        openDayLabel() {
            return this.openDayGroup ? dayMonthFilter(this.openDayGroup.date) : undefined
        },
        columnCount() {
            return this.hasMoreThanOneStore ? 8 : 7
        },
        hasAccessToMoreThanOneChannel() {
            return this.channelListOptions.length > 1
        },
        isFilterOptionByBackendInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_BACKEND_INFO
        },
        isFilterOptionByChannelInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_CHANNEL_INFO
        },
        isFilterOptionByTrackingCodeInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_TRACKING_CODE
        },
        isFilterOptionAdvanced() {
            return this.filterOptionSelected.enum === FILTER_ADVANCED
        },
        detailUrl() {
            return (order) => {
                return `/pedidos/${order.id}`
            }
        },
        customerName() {
            return (order) => {
                const name = order.customer.fullName() || order.customer.corporateName || ''
                const nameSplit = name.split(' ')
                const first = nameSplit[0]
                const last = nameSplit[nameSplit.length-1]

                if (first === last) {
                    return first
                }

                return `${first} ${last}`
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/utils/styles/constants.scss';

$rail-offset: 30px;   // recuo da tabela, abrindo espaço para a trilha
$rail-width: 2px;
$rail-left: -22px;    // x da trilha em relação à primeira célula da linha
// Trilha, regua e marcador sao decoracao, entao usam o roxo de preenchimento
// (--brand-fill), e nao o de texto (--brand): no claro e o mesmo #89138a, no escuro
// o de texto clareia para ficar legivel e, em traco de 2px numa lista longa, pesava.
$rail-color: color-mix(in srgb, var(--brand-fill) 30%, transparent);
$dot-size: 12px;
$dot-left: -27px;     // $rail-left - ($dot-size - $rail-width) / 2, centraliza o marcador na trilha

// Numa lista longa o cinza tem o mesmo peso das bordas da tabela e a separação
// some. A cor da marca resolve, e a régua saturada no topo do cabeçalho é o que
// o olho realmente captura ao varrer rolando.
$day-rule: color-mix(in srgb, var(--brand-fill) 55%, transparent);
$day-tint: color-mix(in srgb, var(--brand-fill) 7%, transparent);
$day-tint-today: color-mix(in srgb, var(--brand-fill) 13%, transparent);

/*
 * Toolbar da listagem: ações à esquerda, alternador de formato ancorado na borda
 * direita da tabela. Mesma linguagem do controle de ordenação de Produtos - o
 * bloco pertence à tabela abaixo, então repete a borda (#dee2e6) e o raio de 2px
 * dela em vez de ficar solto ao lado dos botões.
 */
.order-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}

.order-toolbar-actions {
    display: flex;
    align-items: center;
}

.order-view-mode {
    display: flex;
    align-items: stretch;
    // mantem o bloco ancorado à direita mesmo quando a toolbar quebra em duas linhas
    margin-left: auto;
    border: 1px solid var(--border);
    border-radius: 2px;
    background-color: var(--surface-1);
    overflow: hidden;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

    &:hover {
        border-color: var(--tint-brand-border);
    }

    &:focus-within {
        border-color: var(--brand-soft);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--brand-soft) 12%, transparent);
    }
}

.order-view-mode-caption {
    display: flex;
    align-items: center;
    padding: 0 12px;
    background-color: var(--surface-2);
    border-right: 1px solid var(--border);
    color: var(--text-muted);
    font-size: .8em;
    font-weight: 600;
    letter-spacing: .5px;
    text-transform: uppercase;
    white-space: nowrap;
}

// Botão próprio, sem a classe .btn: o common.scss aplica uppercase e paddings
// dela que não cabem num controle deste tamanho.
.order-view-mode-option {
    appearance: none;
    -webkit-appearance: none;
    height: 34px;
    padding: 0 14px;
    border: 0;
    background-color: transparent;
    color: var(--text-soft);
    font-size: .95em;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color .15s ease-in-out, color .15s ease-in-out;

    &:hover:not(.is-active) {
        background-color: color-mix(in srgb, #{$color-primary} 7%, transparent);
    }

    &:focus {
        outline: 0;
    }

    &.is-active {
        background-color: var(--brand-fill);
        color: var(--text-on-brand);
        font-weight: 600;
        cursor: default;
    }
}

// Toda a trilha vive aninhada aqui: na lista clássica as linhas continuam com a
// classe .order-row (para o zebrado e o hover), e regras soltas fariam elas
// desenharem segmentos de trilha sem trilha nenhuma em volta.
.order-timeline {
    position: relative;
    padding-left: $rail-offset;

    // O marcador e a trilha ficam à esquerda da tabela, fora dela. O overflow do
    // .table-responsive os recortaria, então ele só entra em ação no breakpoint
    // em que a trilha deixa de ser exibida.
    .table-responsive {
        overflow: visible;
    }

    // A trilha é desenhada por linha, e não como um elemento único de altura total.
    // É o que permite tracejar apenas o trecho do dia em aberto sem precisar medir
    // em JS onde o último marcador caiu.
    .timeline-day td:first-child,
    .order-row td:first-child {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            left: $rail-left;
            top: 0;
            bottom: 0;
            width: $rail-width;
            background-color: $rail-color;
        }
    }

    // Num dia em aberto a trilha vira tracejada: o dia não fechou e pode continuar
    // nas próximas páginas.
    .timeline-day.is-open-day td:first-child::before,
    .order-row.is-open-day td:first-child::before {
        background-color: transparent;
        border-left: $rail-width dashed $rail-color;
    }
}

.timeline-day {
    td {
        padding-top: 20px;
        padding-bottom: 7px;
        border-top: 2px solid $day-rule;
        background-color: $day-tint;
        white-space: nowrap;

        // O marcador vai no ::after para pintar por cima do segmento de trilha,
        // que ocupa o ::before da mesma célula.
        &::after {
            content: '';
            position: absolute;
            left: $dot-left;
            top: 50%;
            width: $dot-size;
            height: $dot-size;
            margin-top: -6px;
            border: 2px solid var(--brand-fill);
            border-radius: 50%;
            background-color: var(--surface-1);
        }
    }

    // O cabeçalho é uma divisória, não uma linha clicável: o hover da tabela não
    // pode apagar a cor dele.
    &:hover td {
        background-color: $day-tint;
    }

    // O dia corrente é o único com a régua na cor cheia e o fundo mais carregado.
    &.is-today td {
        border-top-color: var(--brand-fill);
        background-color: $day-tint-today;
    }

    &.is-today:hover td {
        background-color: $day-tint-today;
    }

    &.is-today td::after {
        background-color: var(--brand-fill);
        box-shadow: 0 0 0 4px color-mix(in srgb, var(--brand-fill) 15%, transparent);
    }

    &.is-open-day td::after {
        border-style: dashed;
    }
}

// O primeiro grupo dispensa a régua: o thead já tem a própria borda logo acima,
// e as duas juntas viram linha dupla.
tbody:first-of-type .timeline-day td {
    border-top: 0;
}

// Rabicho tracejado abaixo da tabela, terminando num círculo vazado: a leitura
// visual de que a lista continua.
.timeline-continues {
    position: relative;
    height: 26px;

    &::before {
        content: '';
        position: absolute;
        left: $rail-left;
        top: 0;
        bottom: 8px;
        border-left: $rail-width dashed $rail-color;
    }

    &::after {
        content: '';
        position: absolute;
        left: $dot-left + 2px;
        bottom: 0;
        width: $dot-size - 4px;
        height: $dot-size - 4px;
        border: $rail-width solid $rail-color;
        border-radius: 50%;
    }
}

.timeline-day-label {
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-weight: 700;
    font-size: .85em;
    color: $color-primary;
}

.timeline-day-count {
    float: right;
    // Um tom abaixo do rótulo: a data é o que se procura, a contagem é o apoio.
    // Não desce mais que isto, ou o contraste cai abaixo do mínimo AA.
    color: color-mix(in srgb, #{$color-primary} 85%, transparent);
    font-size: .85em;
    letter-spacing: 1px;

    // O "+" indica contagem parcial, então não pode passar batido.
    &.is-open {
        color: $color-primary;
        font-weight: 600;
        cursor: help;
    }
}

.timeline-open-day-note {
    margin-top: -12px;
    color: var(--text-muted);
    font-size: .9em;
    letter-spacing: 1px;
}

.pagination-day-hint {
    font-weight: 400;
    opacity: .7;
}

// O zebrado do Bootstrap (tbody tr:nth-of-type(odd)) reiniciaria a cada dia
// e ainda contaria a linha de cabeçalho, invertendo as faixas de grupo para
// grupo. Por isso a table-striped foi removida e o zebrado é feito aqui.
.order-row:nth-of-type(even) {
    background-color: color-mix(in srgb, var(--text-strong) 3%, transparent);
}

// A regra acima é mais específica que o .table-hover do Bootstrap, então o
// hover precisa ser redeclarado para continuar valendo nas linhas zebradas.
.order-row:hover,
.order-row:nth-of-type(even):hover {
    background-color: color-mix(in srgb, var(--text-strong) 7.5%, transparent);
}

// Abaixo deste ponto a tabela volta a rolar horizontalmente, e o overflow
// recortaria trilha e marcadores. O dia em aberto continua sinalizado pelo "N+",
// pelo rótulo do botão e pela nota do rodapé.
@media (max-width: 991px) {
    .order-timeline {
        padding-left: 0;

        .table-responsive {
            overflow-x: auto;
        }
    }

    .timeline-continues {
        display: none;
    }

    .timeline-day td:first-child::before,
    .order-row td:first-child::before,
    .timeline-day td::after {
        display: none;
    }

    .timeline-day td {
        border-left: 3px solid var(--brand-fill);
    }

    .timeline-day.is-open-day td {
        border-left-style: dashed;
    }
}
</style>
